import { useCallback, useEffect, useRef, useState } from "react";
import { Archive, ArrowLeft, Compass, Search, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import FooterNav from "../components/FooterNav";
import { getProfile } from "../api/profile";
import { fetchSpaces, joinSpace, type Space } from "../api/spaces";
import SpaceCommunityCard from "../features/spaces/SpaceCommunityCard";
import { readSpacesDirectoryMemory, rememberSpacesDirectoryMemory } from "../features/spaces/listMemory";
import { readSpacesBackState, spacesBackState } from "../features/spaces/navigation";
import { useSpaceQueryState } from "../features/spaces/useSpaceQueryState";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { hasIdentity, requireIdentity } from "../utils/identityGate";

function profileLooksPresence(profile: any) {
    const username = String(profile?.username || "").replace(/^@/, "").trim().toLowerCase();
    return Boolean(
        profile?.isPresence ||
        profile?.realm?.isPresence ||
        profile?.realm?.role === "presence" ||
        profile?.realmRole === "presence" ||
        profile?.role === "presence" ||
        username === "blackspace"
    );
}

function readPresenceHint() {
    try {
        const raw = localStorage.getItem("user") || localStorage.getItem("profile");
        if (!raw) return null;
        return profileLooksPresence(JSON.parse(raw));
    } catch {
        return null;
    }
}

export default function AllSpaces() {
    const navigate = useNavigate();
    const isAnonymous = !hasIdentity();
    const location = useLocation();
    const cachedListRef = useRef(readSpacesDirectoryMemory("all"));
    const restoredScrollRef = useRef(false);
    const mainRef = useRef<HTMLElement | null>(null);
    const [spaces, setSpaces] = useState<Space[]>(() => cachedListRef.current?.spaces || []);
    const [page, setPage] = useState(() => cachedListRef.current?.page || 1);
    const [hasMore, setHasMore] = useState(() => cachedListRef.current?.hasMore ?? true);
    const [loading, setLoading] = useState(() => !cachedListRef.current);
    const [error, setError] = useState<string | null>(null);
    const [joiningId, setJoiningId] = useState<string | null>(null);
    const [searchOpen, setSearchOpen] = useState(Boolean(cachedListRef.current?.searchInput));
    const [identityState, setIdentityState] = useState(() => {
        const hint = readPresenceHint();
        return { ready: hint !== null, isPresence: Boolean(hint) };
    });
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const { searchInput, searchQuery, setSearchInput } = useSpaceQueryState({ debounceMs: 300, initialSearch: cachedListRef.current?.searchInput || "" });
    const backTarget = readSpacesBackState(location.state);
    const currentBackState = spacesBackState(location, "All Spaces");

    useEffect(() => {
        let alive = true;
        getProfile()
            .then((data: any) => {
                if (!alive) return;
                setIdentityState({ ready: true, isPresence: profileLooksPresence({ ...data?.user, realm: data?.realm || data?.user?.realm }) });
            })
            .catch(() => {
                if (alive) setIdentityState((current) => ({ ...current, ready: true }));
            });
        return () => {
            alive = false;
        };
    }, []);

    const loadFirstPage = useCallback(async () => {
        const cachedForQuery = Boolean(cachedListRef.current?.spaces.length && cachedListRef.current.searchQuery === searchQuery);
        setLoading(true);
        setError(null);
        try {
            const data = await fetchSpaces({ page: 1, limit: 30, search: searchQuery, archived: false, excludeJoined: true });
            if (cachedForQuery) {
                const refreshedIds = new Set(data.spaces.map((space) => space.id));
                setSpaces((current) => [...data.spaces, ...current.filter((space) => !refreshedIds.has(space.id))]);
                setPage((current) => Math.max(current, 2));
                setHasMore((current) => current && data.hasMore);
            } else {
                setSpaces(data.spaces);
                setPage(2);
                setHasMore(data.hasMore);
            }
        } catch (err: any) {
            setError(err?.message || "Could not load communities.");
        } finally {
            setLoading(false);
        }
    }, [searchQuery]);

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) return;
        setLoading(true);
        try {
            const data = await fetchSpaces({ page, limit: 30, search: searchQuery, archived: false, excludeJoined: true });
            setSpaces((current) => {
                const known = new Set(current.map((space) => space.id));
                return [...current, ...data.spaces.filter((space) => !known.has(space.id))];
            });
            setPage((current) => current + 1);
            setHasMore(data.hasMore);
        } catch (err: any) {
            setError(err?.message || "Could not load more communities.");
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [hasMore, loading, page, searchQuery]);

    useEffect(() => {
        loadFirstPage();
    }, [loadFirstPage]);

    useEffect(() => {
        if (!searchOpen) return;
        const frame = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus({ preventScroll: true });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [searchOpen]);

    useEffect(() => {
        rememberSpacesDirectoryMemory("all", {
            spaces,
            page,
            hasMore,
            searchInput,
            searchQuery,
        });
    }, [hasMore, page, searchInput, searchQuery, spaces]);

    useEffect(() => {
        const cached = cachedListRef.current;
        if (restoredScrollRef.current || !cached?.scrollTop || !mainRef.current || (loading && !spaces.length)) return;
        restoredScrollRef.current = true;
        window.requestAnimationFrame(() => {
            if (mainRef.current) mainRef.current.scrollTop = cached.scrollTop;
        });
    }, [loading, spaces.length]);

    function rememberScroll(scrollTop = mainRef.current?.scrollTop || 0) {
        rememberSpacesDirectoryMemory("all", { scrollTop });
    }

    function openSpace(id: string) {
        rememberScroll();
        navigate(`/spaces/${id}`, { state: currentBackState });
    }

    function openArchives() {
        rememberScroll();
        navigate("/spaces/archives", { state: currentBackState });
    }

    const sentinelRef = useInfiniteScroll(loadMore, hasMore && !loading);
    const isPresenceIdentity = identityState.isPresence;
    const openOnlySpaceCards = (!isAnonymous && !identityState.ready) || isPresenceIdentity;

    async function handleJoin(spaceId: string) {
        if (isAnonymous) return void requireIdentity("join this Space");
        if (isPresenceIdentity) {
            openSpace(spaceId);
            return;
        }
        if (joiningId) return;
        setJoiningId(spaceId);
        setError(null);
        try {
            const joined = await joinSpace(spaceId);
            setSpaces((current) => current.filter((space) => space.id !== joined.id));
            openSpace(joined.id);
        } catch (err: any) {
            setError(err?.message || "Could not join this community.");
        } finally {
            setJoiningId(null);
        }
    }

    return (
        <div className="bs-mobile-spaces bs-all-spaces-page flex h-screen flex-col overflow-hidden bg-black text-white">
            <header className="bs-all-spaces-header sticky top-0 z-30 px-3 py-3 backdrop-blur-xl sm:px-5">
                <div className="bs-all-spaces-header-inner mx-auto">
                    <div className="bs-all-spaces-shell">
                        <div className="bs-all-spaces-row">
                            <button type="button" onClick={() => navigate(backTarget.path, { replace: true })} className="bs-all-spaces-back" aria-label={`Back to ${backTarget.label}`}>
                                <ArrowLeft size={18} />
                            </button>
                            <div className="bs-all-spaces-title min-w-0 flex-1">
                                <h1>All Spaces</h1>
                                <span>
                                    <Compass size={12} />
                                    {loading && !spaces.length ? "Loading rooms" : `${spaces.length} rooms available`}
                                </span>
                            </div>

                            <div className="bs-all-spaces-actions">
                                <button
                                    type="button"
                                    onClick={() => setSearchOpen((open) => !open)}
                                    className={`bs-all-spaces-command ${searchOpen || searchInput ? "is-active" : ""}`}
                                    aria-label="Search spaces"
                                    aria-expanded={searchOpen}
                                >
                                    <Search size={18} />
                                </button>
                                <button type="button" onClick={openArchives} className="bs-all-spaces-command bs-all-spaces-archive" aria-label="Open archived spaces">
                                    <Archive size={22} strokeWidth={2.45} />
                                    <span className="hidden sm:inline">Archives</span>
                                </button>
                            </div>
                        </div>
                        {searchOpen && (
                            <div className="bs-all-spaces-search-panel">
                                <form
                                    className="bs-spaces-search-shell bs-spaces-mobile-search-form bs-all-spaces-search flex items-center gap-2"
                                    onSubmit={(event) => {
                                        event.preventDefault();
                                        searchInputRef.current?.blur();
                                    }}
                                >
                                    <Search size={17} className="shrink-0 text-cyan-200" />
                                    <input
                                        ref={searchInputRef}
                                        type="search"
                                        value={searchInput}
                                        onChange={(event) => setSearchInput(event.target.value)}
                                        autoComplete="off"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        enterKeyHint="search"
                                        inputMode="search"
                                        placeholder="Search by name, tag, or creator"
                                        className="bs-spaces-search-input bs-mobile-search-input min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none"
                                    />
                                    {searchInput && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchInput("");
                                                searchInputRef.current?.focus();
                                            }}
                                            className="bs-all-spaces-clear"
                                            aria-label="Clear search"
                                        >
                                            <X size={15} />
                                        </button>
                                    )}
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main ref={mainRef} onScroll={(event) => rememberScroll(event.currentTarget.scrollTop)} className="bs-spaces-smooth-list bs-all-spaces-main flex-1 overflow-y-auto">
                <div className="bs-all-spaces-content mx-auto pb-28 pt-6">
                    {error && <div role="alert" className="mb-6 rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-100">{error}</div>}

                    {!loading && spaces.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center">
                            <h2 className="font-black">No communities found</h2>
                            <p className="mt-1 text-sm text-neutral-500">Try a different search.</p>
                        </div>
                    ) : (
                        <div className="bs-all-spaces-grid grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {spaces.map((space) => (
                                <SpaceCommunityCard
                                    key={space.id}
                                    space={space}
                                    onOpen={openSpace}
                                    onJoin={handleJoin}
                                    joining={joiningId === space.id}
                                    authorityView={openOnlySpaceCards}
                                    openOnly={openOnlySpaceCards}
                                />
                            ))}
                            {loading && spaces.length === 0 && Array.from({ length: 8 }).map((_, index) => (
                                <div key={`loading-${index}`} className="h-60 animate-pulse rounded-2xl border border-white/10 bg-neutral-950" />
                            ))}
                        </div>
                    )}

                    <div ref={sentinelRef} className="h-8" aria-hidden="true" />
                    {!hasMore && spaces.length > 0 && <p className="py-5 text-center text-xs font-semibold text-neutral-600">You have reached the end.</p>}
                </div>
            </main>
            <FooterNav />
        </div>
    );
}
